import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Compass, GraduationCap, Bot, Users, Search, Radar, Check, Lock, ArrowRight,
  Sparkles, Clock, Loader2, MapPin,
} from "lucide-react";
import api from "@/lib/api";

const ICONS = { FOUNDATIONS: GraduationCap, COACH: Bot, COPILOT: Users, ANALYST: Search, INTELLIGENCE: Radar };
const STATUS = {
  completed: { label: "Completed", cls: "text-tc-bull border-tc-bull/40 bg-tc-bull/10", Icon: Check },
  current: { label: "You are here", cls: "text-tc-coach border-tc-coach/50 bg-tc-coach/10", Icon: MapPin },
  available: { label: "Available", cls: "text-tc-mastery border-tc-mastery/40 bg-tc-mastery/10", Icon: ArrowRight },
  locked: { label: "Locked", cls: "text-tc-muted border-white/15 bg-white/5", Icon: Lock },
  plan_locked: { label: "Pro & Family plans", cls: "text-tc-xp border-tc-xp/30 bg-tc-xp/5", Icon: Lock },
  coming_soon: { label: "Coming soon", cls: "text-tc-xp border-tc-xp/30 bg-tc-xp/5", Icon: Clock },
};

const PATHS = [
  { key: "beginner", title: "New to trading", desc: "Guide me from the very beginning.", Icon: GraduationCap },
  { key: "some", title: "Some experience", desc: "Assess my skills and find my starting point.", Icon: Compass },
  { key: "experienced", title: "Experienced trader", desc: "Take me straight to the professional tools.", Icon: Search },
];

export default function Roadmap() {
  const navigate = useNavigate();
  const [j, setJ] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/journey").then((r) => setJ(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const choosePath = async (path) => {
    setBusy(true);
    try {
      const { data } = await api.post("/journey/onboard", { path });
      setJ(data);
      if (data.next_action?.to && data.next_action.kind !== "onboard") navigate(data.next_action.to);
    } catch { /* noop */ }
    setBusy(false);
  };

  if (!j) return <div className="p-8 text-tc-muted font-mono text-sm">Loading roadmap…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-2 text-tc-coach text-xs font-mono uppercase tracking-widest"><Compass className="w-3.5 h-3.5" /> How TRADECRAFT works</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Your Roadmap</h1>
        <p className="text-tc-sub mt-1 max-w-2xl text-sm">TRADECRAFT grows with you — from reading markets to supervising professional AI analysis. Here's where you are and where you're going.</p>
      </div>

      {j.first_run && (
        <div className="tc-card p-6 border-tc-coach/30 bg-gradient-to-br from-tc-coach/10 to-transparent" data-testid="path-chooser">
          <div className="text-xs font-mono uppercase tracking-widest text-tc-coach">Choose your path</div>
          <h2 className="font-heading text-xl font-bold mt-1">How do you want to begin?</h2>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {PATHS.map((p) => (
              <button key={p.key} data-testid={`path-${p.key}`} disabled={busy} onClick={() => choosePath(p.key)}
                className="tc-card p-4 text-left hover:-translate-y-0.5 hover:border-tc-coach/40 transition-all disabled:opacity-60">
                <div className="w-9 h-9 rounded-lg border border-tc-coach/25 bg-tc-coach/10 text-tc-coach flex items-center justify-center"><p.Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /></div>
                <div className="font-heading font-semibold mt-2">{p.title}</div>
                <div className="text-sm text-tc-sub mt-1">{p.desc}</div>
              </button>
            ))}
          </div>
          {busy && <div className="mt-3 flex items-center gap-2 text-tc-muted text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Setting up…</div>}
        </div>
      )}

      {j.next_action && (
        <div className="tc-card p-5 border-tc-coach/40 bg-gradient-to-br from-tc-coach/15 to-transparent flex flex-col sm:flex-row sm:items-center gap-4" data-testid="roadmap-next-action">
          <div className="flex-1">
            <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Your next step</div>
            <div className="font-heading text-xl font-bold mt-1">{j.next_action.label}</div>
            <p className="text-tc-sub text-sm mt-1">{j.next_action.why}</p>
          </div>
          <button data-testid="roadmap-next-btn" onClick={() => navigate(j.next_action.to)}
            className="shrink-0 rounded-xl bg-tc-coach text-tc-bg font-bold px-6 py-3 hover:bg-tc-coach/90 transition-colors flex items-center gap-2">
            {j.next_action.label} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Capability ladder */}
      <div className="space-y-3" data-testid="roadmap-ladder">
        {j.stages.map((s, i) => {
          const Icon = ICONS[s.key] || Compass;
          const planLocked = !!s.authorization?.plan_required;
          const st = planLocked ? STATUS.plan_locked : STATUS[s.status] || STATUS.locked;
          const canOpen = (s.status === "current" || s.status === "available") && !planLocked;
          return (
            <div key={s.key} data-testid={`stage-${s.key.toLowerCase()}`}
              className={`tc-card p-5 flex items-start gap-4 border ${s.status === "current" ? "border-tc-coach/40" : "border-white/10"}`}>
              <div className="flex flex-col items-center">
                <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${st.cls}`}><Icon className="w-5 h-5" /></div>
                {i < j.stages.length - 1 && <div className="w-px flex-1 min-h-[16px] bg-white/10 mt-2" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-semibold text-lg">{s.title}</span>
                  <span data-testid={`stage-status-${s.key.toLowerCase()}`} className={`text-[9px] font-mono px-2 py-0.5 rounded border ${st.cls} flex items-center gap-1`}><st.Icon className="w-2.5 h-2.5" /> {st.label}</span>
                </div>
                <p className="text-sm text-tc-sub mt-1">{s.what}</p>
                <p className="text-[12px] text-tc-muted mt-1"><span className="text-tc-mastery font-mono uppercase text-[10px]">Why it matters:</span> {s.why}</p>
                {(s.status === "locked" || planLocked) && s.authorization?.reason && <p className="text-[11px] text-tc-xp mt-1">{s.authorization.reason}</p>}
                {planLocked && <button data-testid={`stage-plans-${s.key.toLowerCase()}`} onClick={() => navigate("/pricing")} className="mt-2 text-tc-xp text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">See plans <ArrowRight className="w-3.5 h-3.5" /></button>}
                {canOpen && s.key === "ANALYST" &&
                  <button data-testid="stage-analyst-cta" onClick={() => navigate("/analyst")} className="mt-2 text-tc-coach text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">Open ANALYST <ArrowRight className="w-3.5 h-3.5" /></button>}
                {canOpen && s.key === "COPILOT" &&
                  <button onClick={() => navigate("/co-analysis")} className="mt-2 text-tc-coach text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">Analyze With Me <ArrowRight className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-tc-muted text-center">Adults get standalone access to professional tools. Younger traders unlock capabilities by demonstrating competency — with guardian authorization.</p>
    </div>
  );
}
